// Angular
import { Injectable } from '@angular/core';

// Capacitor
import { Capacitor } from '@capacitor/core';
import {
  Camera,
  CameraResultType,
  CameraSource
} from '@capacitor/camera';
import { FilePicker } from '@capawesome/capacitor-file-picker';

// Third party
import { BehaviorSubject, Observable } from 'rxjs';
import { LoggerService } from '@amwager/util-ts';

@Injectable()
export class CameraService {

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dataIdSubject: BehaviorSubject<any> = new BehaviorSubject(null);
  dataId$ = this.dataIdSubject.asObservable();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dataBillSubject: BehaviorSubject<any> = new BehaviorSubject(null);
  dataBill$ = this.dataBillSubject.asObservable();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private permissionSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  permission$ = this.permissionSubject.asObservable();

  private stream: MediaStream | null = null;

  private countTryCamera: number = 0;
  private countTryPhotos: number = 0;

  constructor(
    private readonly loggerService: LoggerService,
  ) {

    if (Capacitor.getPlatform() === 'web')
      this.checkCameraPermission();

  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendIdData(data: any) {
    this.dataIdSubject.next( data );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendBillData(data: any) {
    this.dataIdSubject.next( data );
  }

  async takePicture() {

    if(Capacitor.getPlatform() != 'web') await Camera.requestPermissions();

    // Mobile platform: Use Capacitor camera
    await Camera.requestPermissions();
    return Camera.getPhoto({
      quality: 40,
      // allowEditing: false,
      source: CameraSource.Camera,
      resultType: CameraResultType.DataUrl
    }).then((image) => {

      // const blob = this.dataURLtoBlob(image.dataUrl);
      return {
        base64: image.dataUrl,
        base64cut: image.dataUrl?.split(',')[1],
        format: image.format,
        // size: blob.size | 0,
        size: this.convertToAtob(image.dataUrl?.split(',')[1]),
        field: '',
        invalidFileSize: false
      };

    }).catch(() => {
      // this.loggerService.error('Error taking photo', err);
      return null;
    });

  }

  async uploadDocument(isMultiple : boolean = false) {

    return FilePicker.pickFiles({
      types: ['image/png', 'image/jpg', 'image/jpeg', 'image/svg+xml'],
      multiple: isMultiple,
      readData: true
    }).then(async(files) => {

      const f = files;
      const file = f.files[0];

      const fileBlob = this.dataURItoBlob(file.data);
      // file.blob
      const rawFile = new File([fileBlob as BlobPart], file.name, {
        type: file.mimeType,
      });

      // const image = await this.convertFileToBase64(rawFile);
      const image = await this.convertFileToBase64(rawFile);

      return {
        fileName: file.name,
        base64: image,
        base64cut: file.data,
        // base64cut: image.split(',')[1],
        format: file.mimeType,
        size: this.convertToAtob(image.split(',')[1]),
        field: '',
        invalidFileSize: false
      };

    }).catch(() => {
      // this.loggerService.error('Error taking photo', err);
      return null;
    });

  }

  async checkCameraPermissionMobile(): Promise<boolean | null> {
    if (Capacitor.getPlatform() !== 'web') {
      const permissions = await Camera.requestPermissions({permissions: ['camera']});

      if (permissions.camera === 'prompt-with-rationale') {
        this.countTryCamera++;
        return null;
      }
      else if (permissions.camera === 'denied' && this.countTryCamera > 0) {
        this.countTryCamera = 0;
        return null;
      }
      else if (permissions.camera === 'denied' && this.countTryCamera < 1) {
        return false;
      }
      else {
        return permissions.camera === 'granted';
      }
    }
    return true;
  }

  async checkPhotoLibraryPermissionMobile(): Promise<boolean | null> {
    if (Capacitor.getPlatform() !== 'web') {
      const permissions = await Camera.requestPermissions({permissions: ['photos']});

      if (permissions.photos === 'prompt-with-rationale') {
        this.countTryPhotos++;
        return null;
      }
      else if (permissions.photos === 'denied' && this.countTryPhotos > 0) {
        this.countTryPhotos = 0;
        return null;
      }
      else if (permissions.photos === 'denied' && this.countTryPhotos < 1) {
        return false;
      }
      else {
        return permissions.photos === 'granted';
      }
    }
    return true;
  }

  async takePictureMobile() {
    const hasPermission = await this.checkCameraPermissionMobile();
    if (hasPermission === null) return null;
    if (!hasPermission) return false;

    try {
      const image = await Camera.getPhoto({
        quality: 40,
        source: CameraSource.Camera,
        resultType: CameraResultType.DataUrl
      });

      return {
        base64: image.dataUrl,
        base64cut: image.dataUrl?.split(',')[1],
        format: image.format,
        size: this.convertToAtob(image.dataUrl?.split(',')[1]),
        field: '',
        invalidFileSize: false
      };
    } catch (error) {
      // this.loggerService.error('Error taking photo:', error);
      return null;
    }
  }

  async uploadDocumentMobile() {
    const hasPermission = await this.checkPhotoLibraryPermissionMobile();
    if (hasPermission === null) return null;
    if (!hasPermission) return false;

    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Photos
      });

      if (!photo.base64String) return null;

      const format = photo.format || 'jpeg';
      const base64 = `data:image/${format};base64,${photo.base64String}`;

      return {
        fileName: `image_${Date.now()}.jpg`,
        base64: base64,
        base64cut: photo.base64String,
        format: format,
        size: this.convertToAtob(photo.base64String),
        field: '',
        invalidFileSize: false
      };
    } catch (error) {
      this.loggerService.error('Error picking file:', error);
      return null;
    }
  }

  // async uploadDocument() {

  //   if(Capacitor.getPlatform() != 'web') await Camera.requestPermissions();
  //   return Camera.getPhoto({
  //     quality: 40,
  //     allowEditing: false,
  //     source: CameraSource.Photos,
  //     resultType: CameraResultType.DataUrl
  //   }).then((image) => {

  //     // const blob = this.dataURLtoBlob(image.dataUrl);
  //     return {
  //       base64: image.dataUrl,
  //       base64cut: image.dataUrl?.split(',')[1],
  //       format: image.format,
  //       // size: blob.size | 0,
  //       size: this.convertToAtob(image.dataUrl?.split(',')[1]),
  //       field: '',
  //       invalidFileSize: false
  //     };

  //   }).catch(err => {
  //     this.loggerService.error('Error taking photo', err);
  //     return null;
  //   });

  // }

  private convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dataURItoBlob(dataURI: any) {
    const byteString = window.atob(dataURI);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([int8Array], { type: 'mime' });
    return blob;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // private dataURLtoBlob(dataurl: any) {
  //   const arr = dataurl.split(',');
  //   const mime = arr[0].match(/:(.*?);/)[1];
  //   const bstr = atob(arr[1]);
  //   let n = bstr.length;
  //   const u8arr = new Uint8Array(n);

  //   while(n--){
  //       u8arr[n] = bstr.charCodeAt(n);
  //   }

  //   return new Blob([u8arr], {type:mime});
  // }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private convertToAtob(base64: any) {
    // Convert the Base64 string into binary data
    const binaryString = atob(base64);

    // Calculate the size of the binary data
    const fileSizeInBytes = binaryString.length;

    // If you want to convert bytes to other units like KB or MB, you can do so like this:
    // const fileSizeInKB = fileSizeInBytes / 1024;
    // const fileSizeInMB = fileSizeInKB / 1024;

    return (fileSizeInBytes / (1024 * 1024));
  }

  //  duplicate of above convertToAtob,   this is just more specific so it won't display things like 0.3mb
  private convertToAtobDynamic(base64: string): string {
    // Convert Base64 to binary data
    const binaryString = atob(base64);

    // Calculate the size in bytes
    const fileSizeInBytes = binaryString.length;

    // Convert bytes to KB
    const fileSizeInKB = fileSizeInBytes / 1024;

    // Convert KB to MB
    const fileSizeInMB = fileSizeInKB / 1024;

    // ✅ Display in KB if less than 1MB, otherwise display in MB
    return fileSizeInMB >= 1
      ? `${fileSizeInMB.toFixed(2)} MB`
      : `${Math.ceil(fileSizeInKB)} KB`;
  }

  private async checkCameraPermission(): Promise<void> {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
      this.permissionSubject.next(permissionStatus.state);

      // Listen for permission state changes
      permissionStatus.onchange = () => {
        this.permissionSubject.next(permissionStatus.state);
      };
    } catch (error) {
      this.loggerService.error('Error checking camera permission:', error);
      this.permissionSubject.next('error');
    }

  }

  // Observable to get the current permission state
  getCameraPermissionStatus(): Observable<string> {
    return this.permissionSubject.asObservable();
  }

  // Start camera and attach stream to a video element
  async startCamera(videoElement?: HTMLVideoElement): Promise<void> {

    if (!videoElement) {
      this.loggerService.warn('No video element provided. Camera will not start.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.stream = stream;
      videoElement.srcObject = stream;
      await videoElement.play();
    } catch (error) {
      this.loggerService.error('Error accessing camera:', error);
      throw new Error('Camera access denied or not available.');
    }
  }

  // Capture image from video element
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  captureSnapshot(videoElement: HTMLVideoElement) : any {
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;

    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/png'); // Base64 Image

      const base64cut = dataUrl.split(',')[1]; // Extract only the base64 data
      const size = this.convertToAtobDynamic(base64cut); // ✅ Reuse existing function

      return {
        base64: dataUrl,
        base64cut: base64cut,
        format: 'png',
        field: '',
        size: size,
        invalidFileSize: false,
        filename : "Photo Taken"
      };
    }
    throw new Error('Unable to capture snapshot.');
  }

  // Stop the camera stream
  stopCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }



}
